import React, { useRef, useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import { useAssistantChat } from "./hooks/useAssistantChat";
import { ChatIntroduction } from "./components/ChatIntroduction";
import { SuggestedPrompts } from "./components/SuggestedPrompts";
import { MessageBubble } from "./components/MessageBubble";
import { ChatInput } from "./components/ChatInput";
import { GuidedQuizSetup } from "./components/GuidedQuizSetup";
import { GuidedAssignmentSetup } from "./components/GuidedAssignmentSetup";
import { CreationActions } from "./components/CreationActions";
import { Typography } from "@mui/material";
import GeneralLoading from "../../shared-components/Loading";
import { useApiClient } from "../../api";
import { SchoolClassSelectDialog } from "../school/components/SchoolClassSelectDialog";

const CHAT_HEIGHT = {
  full: "calc(100vh - 150px)",
  embedded: "100%",
  card: "100%",
};

export const AssistantChat = ({
  initialContext = {},
  mode = "full",
  conversationId = null,
  initialMessages = null,
  initialRequest = null,
  onConversationId = null,
}) => {
  const {
    messages,
    isLoading,
    sendMessage,
    retryMessage,
    retryAvailable,
    setMessages,
    context,
    selectClass,
    updateGuidedSetup,
    className,
    creationOptions,
    creationOptionsError,
    conversationId: activeConversationId,
  } = useAssistantChat(initialContext, conversationId, mode, initialRequest);
  const apiClient = useApiClient();
  const messagesContainerRef = useRef(null);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [selectClassOpen, setSelectClassOpen] = useState(false);
  const [selectionKind, setSelectionKind] = useState(null);
  const needsClass =
    context.creation && !context.class_id && !context.assignment_id;

  const guided =
    ["assignment", "quiz"].includes(context.creation?.kind) &&
    (!activeConversationId || Object.hasOwn(context, "guided_setup"));

  const parseData = (value) => {
    if (!value || typeof value !== "string") return value || null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  const loadUsageStats = async () => {
    try {
      const stats = await apiClient.assistant.getUsageStats();
      setUsageStats(stats);
    } catch (error) {
      console.error("Failed to load usage stats:", error);
    }
  };

  // Fetch usage stats on mount
  useEffect(() => {
    loadUsageStats();
  }, []);

  useEffect(() => {
    if (onConversationId && activeConversationId) {
      onConversationId(activeConversationId);
    }
  }, [activeConversationId, onConversationId]);

  // Reset messages when conversationId changes (new conversation) - only in full mode
  useEffect(() => {
    if (mode === "full") {
      setMessagesLoaded(false);
      setMessages([]);
    }
  }, [conversationId, setMessages, mode]);

  // Load initial messages if provided
  useEffect(() => {
    if (initialMessages && initialMessages.length > 0 && !messagesLoaded) {
      const formattedMessages = initialMessages.map((msg) => ({
        role: msg.isResponse ? "assistant" : "user",
        content: msg.message,
        timestamp: new Date(msg.sentAt),
        data: parseData(msg.dslQuery),
      }));
      setMessages(formattedMessages);
      setMessagesLoaded(true);
    }
  }, [initialMessages, messagesLoaded, setMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSuggestedPromptClick = (promptText) => {
    handleSendMessage(promptText, { isPromptUsage: true });
  };

  const handleSendMessage = async (text, options) => {
    await sendMessage(text, options);
    await loadUsageStats(); // Refresh stats after sending
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: CHAT_HEIGHT[mode] ?? "100%",
        backgroundColor: "background.default",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <SchoolClassSelectDialog
        open={selectClassOpen}
        setOpen={setSelectClassOpen}
        quiz={(context.creation?.kind || selectionKind) === "quiz"}
        onSelect={(schoolClass) => selectClass(schoolClass, selectionKind)}
      />
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          pt: 2,
          px: 2,
        }}
      >
        {messages.length === 0 && (
          <>
            {context.creation ? (
              <Typography>
                {context.creation.kind === "assignment"
                  ? "Let’s create your assignment. I’ll guide you through the details, then you can review your draft before publishing."
                  : "Let’s create your quiz. I’ll guide you through the details, then you can review your draft before publishing."}
              </Typography>
            ) : (
              <ChatIntroduction />
            )}
            {!context.creation && (
              <SuggestedPrompts
                onSelect={handleSuggestedPromptClick}
                context={initialContext}
              />
            )}
          </>
        )}

        {needsClass && (
          <Box sx={{ my: 2 }}>
            <Typography>First, select the class this is for.</Typography>
            <Button
              variant="outlined"
              disabled={isLoading}
              onClick={() => setSelectClassOpen(true)}
            >
              Select class
            </Button>
          </Box>
        )}
        {context.creation && className && (
          <Typography variant="body2" sx={{ my: 1 }}>
            Creating for {className}
          </Typography>
        )}

        {guided &&
          context.class_id &&
          context.creation.kind === "assignment" && (
            <GuidedAssignmentSetup
              key={context.class_id}
              value={context.guided_setup}
              onChange={updateGuidedSetup}
              initial={context.creation}
              options={creationOptions}
              error={creationOptionsError}
              busy={isLoading || retryAvailable}
              onSubmit={(creation) =>
                sendMessage(
                  "Prepare my assignment proposal from these question details.",
                  { creation },
                )
              }
            />
          )}

        {guided &&
          (context.class_id || context.assignment_id) &&
          context.creation.kind === "quiz" && (
            <GuidedQuizSetup
              key={context.class_id}
              value={context.guided_setup}
              onChange={updateGuidedSetup}
              initial={context.creation}
              busy={isLoading || retryAvailable}
              onSubmit={(creation) =>
                sendMessage("Generate my quiz from these details.", {
                  creation,
                })
              }
            />
          )}

        {messages.map((msg, i) => (
          <MessageBubble key={i} message={msg} />
        ))}

        {isLoading && (
          <Box sx={{ px: 2, py: 1 }}>
            <GeneralLoading message="Thinking..." direction="row" size={20} />
          </Box>
        )}

        <CreationActions
          conversationId={activeConversationId}
          refreshKey={messages.length}
          onSelectClass={
            !context.class_id && !context.assignment_id && !needsClass
              ? (kind) => {
                  setSelectionKind(kind);
                  setSelectClassOpen(true);
                }
              : null
          }
        />
        <div />
      </Box>

      {retryAvailable && (
        <Button
          disabled={isLoading}
          onClick={async () => {
            await retryMessage();
            await loadUsageStats();
          }}
        >
          Retry last request
        </Button>
      )}

      {!guided && (
        <ChatInput
          onSend={handleSendMessage}
          disabled={isLoading || Boolean(needsClass)}
          usageStats={usageStats}
          allowCreation
        />
      )}
    </Box>
  );
};
